// Shared in-app navigation blocker for unsaved / in-flight record edits.
//
// The app uses a plain <BrowserRouter> (react-router v7, no data router), so
// useBlocker is unavailable and there is no injectable history object. This
// helper guards the two navigation channels a user drives directly:
//   1. same-origin anchor clicks (shell links, row links) - capture phase;
//   2. browser back/forward (popstate) - window capture listener that runs
//      before the router's own listener, stops it, and restores the entry
//      using react-router's history.state.idx (falls back to pushState).
// Programmatic navigation is guarded at the existing router navigator while
// an editor is mounted. This does not introduce a second location store.
//
// A guard is { isBlocking(): boolean, onBlocked(proceed: () => void) }.
// proceed() replays the blocked navigation once, bypassing all guards.

const guards = new Set();
let bypass = false;
let ignorePops = 0;
let allowPops = 0;
let lastIdx = null;
let installed = false;
const navigators = new Map();

const histIdx = () => {
  const idx = typeof window !== "undefined" ? window.history.state?.idx : null;
  return Number.isInteger(idx) ? idx : null;
};

function blockingGuard() {
  if (bypass) return null;
  for (const g of guards) { try { if (g.isBlocking()) return g; } catch { /* guard errors never block */ } }
  return null;
}

/** Guard the plain BrowserRouter's imperative navigation channels as well. */
export function guardRouterNavigator(navigator) {
  if (!navigator) return () => {};
  let entry = navigators.get(navigator);
  if (!entry) {
    const originals = {};
    const wrappers = {};
    for (const method of ["push", "replace", "go"]) {
      if (typeof navigator[method] !== "function") continue;
      originals[method] = navigator[method];
      wrappers[method] = function (...args) {
        const proceed = () => {
          const previous = bypass;
          bypass = true;
          try { return originals[method].apply(navigator, args); }
          finally { bypass = previous; }
        };
        const guard = blockingGuard();
        if (guard) { guard.onBlocked(proceed); return; }
        return proceed();
      };
      navigator[method] = wrappers[method];
    }
    entry = { count: 0, originals, wrappers };
    navigators.set(navigator, entry);
  }
  entry.count += 1;
  return () => {
    entry.count -= 1;
    if (entry.count) return;
    for (const method of Object.keys(entry.originals)) {
      if (navigator[method] === entry.wrappers[method]) navigator[method] = entry.originals[method];
    }
    navigators.delete(navigator);
  };
}

/** Only call after explicit discard approval and a fresh in-flight check. */
export function proceedConfirmedNavigation(proceed) {
  const previous = bypass;
  bypass = true;
  try { return proceed(); } finally { bypass = previous; }
}

/** Pure: should this click on `anchor` be treated as an in-app navigation? */
export function isGuardedAnchorClick(event, anchor, origin = window.location.origin) {
  if (!anchor || event.defaultPrevented) return false;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return false;
  if (anchor.closest("[data-nav-guard-ignore]")) return false;
  const raw = anchor.getAttribute("href");
  if (!raw || raw.startsWith("#") || /^(mailto|tel|javascript):/i.test(raw)) return false;
  let url;
  try { url = new URL(anchor.href, origin); } catch { return false; }
  return url.origin === origin;
}

function onClickCapture(e) {
  const anchor = e.target?.closest?.("a[href]");
  if (!isGuardedAnchorClick(e, anchor)) return;
  const g = blockingGuard();
  if (!g) return;
  e.preventDefault();
  e.stopPropagation();
  g.onBlocked(() => { bypass = true; try { anchor.click(); } finally { bypass = false; } });
}

function onPopCapture(e) {
  if (ignorePops > 0) { ignorePops -= 1; e.stopImmediatePropagation(); return; }
  if (allowPops > 0) { allowPops -= 1; lastIdx = histIdx(); return; }
  const g = blockingGuard();
  if (!g) { lastIdx = histIdx(); return; }
  e.stopImmediatePropagation();
  const now = histIdx();
  const delta = now != null && lastIdx != null ? now - lastIdx : null;
  if (delta) { ignorePops += 1; window.history.go(-delta); }
  else window.history.pushState(window.history.state, "", window.location.href);
  // pushState fallback cannot replay; the user stays on the current entry.
  g.onBlocked(() => { if (delta) { allowPops += 1; window.history.go(delta); } });
}

function install() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  document.addEventListener("click", onClickCapture, true);
  window.addEventListener("popstate", onPopCapture, true);
}
function uninstall() {
  if (!installed || guards.size) return;
  installed = false;
  document.removeEventListener("click", onClickCapture, true);
  window.removeEventListener("popstate", onPopCapture, true);
}

/** Register a guard; returns an unregister function. */
export function registerNavigationGuard(guard) {
  guards.add(guard);
  lastIdx = histIdx();
  install();
  return () => { guards.delete(guard); uninstall(); };
}

/** Record the current history index after each router location change. */
export function syncNavigationIndex() { lastIdx = histIdx(); }

/** Test seam. */
export function _resetNavigationGuards() { guards.clear(); bypass = false; ignorePops = 0; allowPops = 0; uninstall(); }
